import { FooterV2 } from "@components/V2/Footer";
import SubscribeEmail from "@components/V2/SubscribeEmail";
import { ETH, ETHX, STETH } from "@config/contractsConfig";
import useTotalTVL from "@hooks/useTotalTvl";
import {
  Hero,
  HeroCalculator,
  PartnersLogoContainer,
} from "@kelp-dao/kernel-design-system";
import router from "next/router";
import { IMAGES } from "@constants/images";
import React, { useEffect } from "react";
import { getImageWithSubRoute, numberToCurrency, trackEvent } from "@lib/utils";
import { useRestakeApy } from "@hooks/useRestakeApy";
import { LP_RESTAKE_WIDGET_CLICK } from "@constants/analytics";
import { getUrlForLocalImage } from "../../lib/utils";
import FeaturesSection from "@components/V2/Landing/FeaturesSection";
import BaseImage from "@components/BaseImage";

interface LandingProps {
  preCalculatedAPY?: {
    formattedAPY: string;
    calculatedTotalAPY: number;
  };
}

const Landing: React.FC<LandingProps> = () => {
  const { totalStaked } = useTotalTVL();

  // Get APY data - use pre-calculated values from SSG if available, otherwise from hook
  const hookData = useRestakeApy();
  const formattedAPY = hookData?.roundedTotalAPY;
  const calculatedTotalAPY = hookData?.calculatedTotalAPY || 0;
  const isLoading = hookData.isLoading;

  const handleHeroButtonClick = () => {
    trackEvent("lp_product_cta_click", {
      event_type: "button_click",
      event_category: "Landing",
      button_text: "Restake now",
      location: "body",
    });

    router.push("/restake");
  };

  const handlePartnersButtonClick = () => {
    trackEvent("lp_body_clicks", {
      event_type: "button_click",
      event_category: "Landing",
      location: "body",
      button_text: "View Ecosystem",
    });
    window.open("https://kerneldao.com/ecosystem/", "_blank");
  };

  useEffect(() => {
    trackEvent("pages", {
      event_type: "page_view",
      event_category: "Landing",
      app: "kelp",
    });
  }, []);

  return (
    <>
      <div className="flex flex-col">
        <Hero
          className="lg:!min-h-[calc(100vh-160px)] xl:!min-h-[calc(100vh-100px)] 3xl:!min-h-[720px] lg:!py-0 xl:!pb-20 sm:!pb-0 md:!pb-14 lg:!pb-0 lg:pr-[40px] xl:pr-[80px]"
          appType={"kelp"}
          apr={formattedAPY}
          tvl={
            totalStaked ? `$${numberToCurrency(totalStaked.toString())}` : "-"
          }
          onHeroButtonClick={handleHeroButtonClick}
          animationComponent={
            <div className="w-auto h-auto md:mt-[-50px] lg:mt-[-130px] lg:max-w-[600px] xl:max-w-[700px]">
              <video
                autoPlay
                muted
                playsInline
                className="w-full h-full object-contain"
              >
                <source
                  src={getUrlForLocalImage("/assets/hero/kelp-hero.mp4")}
                  type="video/mp4"
                ></source>
              </video>
            </div>
          }
          leftContainerClassName="max-w-full lg:!max-w-[500px] xl:!max-w-[700px] lg:min-w-[300px] xl:min-w-[500px]"
          rightContainerClassName="max-w-full md:!max-w-full md:min-w-[400px] lg:!max-w-[600px] xl:!max-w-[700px]"
          rightContentContainerClassName="max-w-full md:!max-w-[450px] lg:!max-w-[600px] xl:!max-w-[700px]"
          headingClassName="w-[348px] md:!w-[542px] lg:!w-[524px] xl:!w-full"
          bodyClassName="w-[236px] md:!w-full lg:!w-[382px] xl:!w-full"
        />
        <div>
          <HeroCalculator
            className="justify-between lg:p-20 xl:px-40 lg:!pt-0 lg:max-w-5xl xl:max-w-[1536px] items-center w-full mx-auto xl:gap-20"
            heading="Explore Kelp"
            subheading="Live on 10+ chains"
            isLoadingApy={isLoading}
            apy={calculatedTotalAPY}
            cards={[
              {
                title: "Audits",
                description: "Secured by trusted auditors",
                icon: (
                  <BaseImage
                    src="/assets/hero/audits.png"
                    width={38}
                    height={38}
                    alt="Audits"
                  />
                ),
                onArrowClick: () => {
                  trackEvent("lp_body_clicks", {
                    event_type: "button_click",
                    event_category: "Landing",
                    location: "body",
                    button_text: "Audits",
                    destination_url:
                      "https://kerneldao.gitbook.io/kernel/getting-started/kelp/audits/",
                  });
                  window.open(
                    "https://kerneldao.gitbook.io/kernel/getting-started/kelp/audits/",
                    "_blank"
                  );
                },
              },
              {
                title: "Bug Bounty",
                description: "Secure Kelp, earn up to $250K",
                icon: (
                  <BaseImage
                    src="/assets/hero/bug.png"
                    width={38}
                    height={38}
                    alt="Bug Bounty"
                  />
                ),
                onArrowClick: () => {
                  trackEvent("lp_body_clicks", {
                    event_type: "button_click",
                    event_category: "Landing",
                    location: "body",
                    button_text: "Bug Bounty",
                    destination_url:
                      "https://immunefi.com/bug-bounty/kelp-dao/information/",
                  });
                  window.open(
                    "https://immunefi.com/bug-bounty/kelp-dao/information/",
                    "_blank"
                  );
                },
              },
            ]}
            calculator={{
              tokenSymbol: "rsETH",
              placeholder: "100",
              restakeButtonText: "Restake now",
              onRestakeClick: (amount, token) => {
                trackEvent(LP_RESTAKE_WIDGET_CLICK, {
                  token_symbol: token.id,
                  token_amount: amount,
                });
                router.push(`/restake?amount=${amount}&token=${token.id}`);
              },
              coins: [
                {
                  id: ETH,
                  label: ETH,
                  iconSrc: getImageWithSubRoute(IMAGES.TOKENS.ETH),
                },
                {
                  id: STETH,
                  label: STETH,
                  iconSrc: getImageWithSubRoute(IMAGES.TOKENS.STETH),
                },
                {
                  id: ETHX,
                  label: ETHX,
                  iconSrc: getImageWithSubRoute(IMAGES.TOKENS.ETHX),
                },
              ],
            }}
          />
          <FeaturesSection title="ETH works, rsETH outworks" />
          <div className="px-2 md:px-0">
            <PartnersLogoContainer
              appName="Kelp"
              buttonAction={handlePartnersButtonClick}
            />
          </div>
          <SubscribeEmail />
        </div>
        <div className="flex flex-col gap-4 my-4"></div>
      </div>
      <FooterV2 />
    </>
  );
};

export default Landing;
